import useApi from 'common/hooks/useApi';
import useTypedSelector from 'common/hooks/useTypedSelector';
import ProfileVerificationStatusCd from 'domain/enums/ProfileVerificationStatusCd';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import FormVerificationResponse from './FormVerificationResponse';
import getVerificationStatus from './get-verification-status';
import { ReuploadLocationState } from './ReuploadLocationState';
import VerifyIdentityPresenter from './VerifyIdentityPresenter';

function VerifyIdentityContainer() {
  const BASE_URL = process.env.REACT_APP_API_BASE;
  const { access_token } = useTypedSelector((state) => state.auth);
  const [fileSelected, setFileSelected] = React.useState<File>();
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [success, setSuccess] = React.useState<boolean>();
  const { location } = useHistory<ReuploadLocationState>();

  // Get verification status of logged in user
  const [
    { data, loading },
    fetchVerification,
  ] = useApi<FormVerificationResponse>({
    url: `/profile/formVerification`,
    method: 'GET',
  });

  useEffect(() => {
    fetchVerification();
  }, [fetchVerification]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;
    setFileSelected(fileList[0]);
  };

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    try {
      setUploading(true);
      const target = e.target as typeof e.target & {
        attachmentTypeCd: { value: string };
        // files handled in state, ignore here
      };
      const attachmentTypeCd = target.attachmentTypeCd.value;
      const formData = new FormData();
      formData.append('attachmentTypeCd', attachmentTypeCd);
      if (fileSelected) {
        formData.append('files', fileSelected, fileSelected.name);
      }
      const headers: RequestInit['headers'] = {};
      if (access_token) {
        headers['Authorization'] = `Bearer ${access_token}`;
      }
      const response = await fetch(`${BASE_URL}/attachments/identity`, {
        method: 'POST',
        body: formData,
        headers,
      });
      const data = await response.json();
      if (data && data.status >= 400 && data.error) {
        setError(getCustomError(data));
      } else {
        setError(undefined);
        setSuccess(true);
      }
    } finally {
      setUploading(false);
    }
  };

  function getCustomError(data?: { error?: string; message?: string }) {
    if (data) {
      if (data.message) {
        return data.message;
      } else if (data.error) {
        return data.error;
      }
    }
    return 'Unhandled error.';
  }

  const status = data
    ? getVerificationStatus(
        data.status,
        data.attachment != null,
        data.hasSubmittedApps
      )
    : null;

  return (
    <>
      <VerifyIdentityPresenter
        locationState={location.state}
        loading={loading}
        onSubmit={onSubmit}
        handleFileChange={handleFileChange}
        hasPostDraftApplications={data?.hasSubmittedApps || false}
        saving={uploading}
        postSuccess={success}
        postError={error}
        allowedFileTypes={data?.allowedFileTypes || null}
        attachment={data?.attachment || null}
        status={status}
        canReupload={
          data ? canReupload(data.status, data.hasSubmittedApps) : false
        }
      />
    </>
  );
}

function canReupload(
  status: ProfileVerificationStatusCd,
  hasPostDraftApps: boolean
) {
  // Allow users to reupload when verification is rejected
  if (status === ProfileVerificationStatusCd.REJECTED) {
    return true;
  }
  // Don't allow users to reupload when they have approved documents
  if (status === ProfileVerificationStatusCd.APPROVED) {
    return false;
  }
  // Don't allow re-upload if user has precheck pending (or above) applications
  if (hasPostDraftApps) {
    return false;
  }

  return true;
}

export default VerifyIdentityContainer;
